import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { groupBy, pipe, values, flatten, reject, eq, defer, entries } from "lodash/fp";
import { ListboxWrapper, ListboxHeading, ListboxSearch, ListboxList, ListboxGroup, ListboxGroupHeading, ListboxVoid } from "./listbox.styles.es.js";
import { ListboxOption } from "./listbox-option.es.js";
import { Input } from "../input/input.es.js";
import "@reach/auto-id";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import { useMouseTracker } from "../../hooks/use-mouse-tracker.es.js";
import { useUpdateEffect } from "@reach/utils";
import { useOnClickOutside } from "../../hooks/use-on-click-outside.es.js";
import "@reach/rect";
import "compute-scroll-into-view";
import { useTimeouts } from "../../hooks/use-timeouts.es.js";
import { deprecated } from "../../prop-types-validators/deprecated.es.js";
const propTypes = {
  alwaysHighlight: PropTypes.bool,
  appearance: PropTypes.oneOf(["neat", "none", "round"]),
  autoFocus: PropTypes.bool,
  buttonRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }),
  closeOnBlur: PropTypes.bool,
  closeOnClickOutside: PropTypes.bool,
  closeOnSelect: PropTypes.bool,
  defaultQuery: PropTypes.string,
  emptyText: deprecated(PropTypes.string, "Use itemsLabel prop instead."),
  groupKey: PropTypes.string,
  groupVariant: PropTypes.oneOf(["divider", "header"]),
  heading: PropTypes.string,
  height: PropTypes.number,
  infoKey: PropTypes.string,
  itemsLabel: PropTypes.string,
  labelKey: PropTypes.string,
  maxHeight: PropTypes.number,
  multi: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onSearch: PropTypes.func,
  options: (props, ...rest) => PropTypes.arrayOf(
    PropTypes.shape({
      [props.labelKey]: PropTypes.string,
      [props.valueKey]: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]).isRequired
    })
  ).isRequired(props, ...rest),
  placeholder: PropTypes.string,
  renderItem: PropTypes.func,
  search: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  valueKey: PropTypes.string,
  values: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  width: PropTypes.number
};
const defaultProps = {
  alwaysHighlight: false,
  appearance: "round",
  autoFocus: false,
  buttonRef: void 0,
  closeOnBlur: false,
  closeOnClickOutside: false,
  closeOnSelect: false,
  defaultQuery: "",
  emptyText: void 0,
  groupKey: void 0,
  groupVariant: "header",
  heading: void 0,
  height: void 0,
  infoKey: "info",
  itemsLabel: "items",
  labelKey: "label",
  maxHeight: 390,
  multi: false,
  onClose: void 0,
  onSearch: void 0,
  options: [],
  placeholder: void 0,
  renderItem: void 0,
  search: false,
  value: void 0,
  valueKey: "value",
  values: [],
  width: void 0
};
const Listbox = ({
  alwaysHighlight,
  appearance,
  autoFocus,
  buttonRef,
  closeOnBlur,
  closeOnClickOutside,
  closeOnSelect,
  defaultQuery,
  emptyText,
  groupKey,
  groupVariant,
  heading,
  height,
  infoKey,
  itemsLabel,
  labelKey,
  maxHeight,
  multi,
  onChange,
  onClose,
  onSearch,
  options,
  placeholder,
  renderItem,
  search,
  value,
  valueKey,
  values: values$1,
  width
}) => {
  const ref = useRef();
  const listRef = useRef();
  const searchRef = useRef();
  const mouseTracker = useMouseTracker();
  const addTimeout = useTimeouts();
  const [highlightedIndex, setHighlightedIndex] = useState(
    alwaysHighlight ? 0 : -1
  );
  const [query, setQuery] = useState(defaultQuery);
  const showGroupHeading = Boolean(groupKey) && groupVariant === "header";
  const filteredOptions = useMemo(
    () => options.filter(
      (opt) => String(opt[labelKey] || opt[valueKey] || "").toLowerCase().includes(query.toLowerCase())
    ),
    [labelKey, options, query, valueKey]
  );
  const groupedOptions = useMemo(
    () => groupBy(groupKey, filteredOptions),
    [filteredOptions, groupKey]
  );
  const enabledOptions = useMemo(
    () => pipe(values, flatten, reject("disabled"))(groupedOptions),
    [groupedOptions]
  );
  const handleSelect = useCallback(
    (val) => {
      if (!multi) {
        onChange(val);
        if (closeOnSelect && onClose) {
          onClose();
        }
      } else if (values$1.includes(val)) {
        onChange(reject(eq(val), values$1));
      } else {
        onChange([...values$1, val]);
      }
    },
    [closeOnSelect, multi, onChange, onClose, values$1]
  );
  const handleWrapperKeyDown = useCallback(
    (event) => {
      const maxIndex = enabledOptions.length - 1;
      event.stopPropagation();
      switch (event.key) {
        case "ArrowDown":
          setHighlightedIndex((index) => Math.min(index + 1, maxIndex));
          break;
        case "ArrowUp":
          setHighlightedIndex((index) => Math.max(0, index - 1));
          break;
        case "Enter":
          if (highlightedIndex >= 0) {
            const item = enabledOptions[highlightedIndex];
            if (item && !item.disabled) {
              handleSelect(item[valueKey]);
            }
          }
          break;
        case "Escape":
          if (buttonRef && buttonRef.current) {
            buttonRef.current.focus();
          } else if (onClose) {
            onClose();
          }
          break;
      }
    },
    [
      buttonRef,
      enabledOptions,
      handleSelect,
      highlightedIndex,
      onClose,
      valueKey
    ]
  );
  const handleBlur = useCallback(() => {
    if (!mouseTracker.isMouseDown && closeOnBlur) {
      addTimeout(() => {
        if (!ref.current.contains(document.activeElement) && onClose) {
          onClose();
        }
      }, 150);
      setHighlightedIndex(alwaysHighlight ? 0 : -1);
    }
  }, [
    addTimeout,
    alwaysHighlight,
    closeOnBlur,
    mouseTracker.isMouseDown,
    onClose
  ]);
  const handleMouseLeave = useCallback(() => {
    setHighlightedIndex(alwaysHighlight ? 0 : -1);
  }, [alwaysHighlight]);
  const handleInputChange = useCallback(
    (searchValue) => {
      setHighlightedIndex(alwaysHighlight ? 0 : -1);
      setQuery(searchValue);
    },
    [alwaysHighlight]
  );
  useUpdateEffect(() => {
    if (onSearch) {
      onSearch(query);
    }
  }, [onSearch, query]);
  useEffect(() => {
    const { current: node } = search ? searchRef : ref;
    if (autoFocus) {
      defer(() => {
        if (node) {
          node.focus();
        }
      });
    }
  }, [autoFocus, search]);
  useOnClickOutside(
    useMemo(() => [ref, buttonRef], [buttonRef]),
    useCallback(() => {
      if (closeOnClickOutside && onClose) {
        onClose();
      }
    }, [closeOnClickOutside, onClose])
  );
  return /* @__PURE__ */ jsxs(
    ListboxWrapper,
    {
      ref,
      appearance,
      "data-testid": "listbox-wrapper",
      height,
      maxHeight,
      onBlur: handleBlur,
      onKeyDown: handleWrapperKeyDown,
      onMouseLeave: handleMouseLeave,
      tabIndex: !search ? -1 : void 0,
      width,
      children: [
        heading && /* @__PURE__ */ jsx(ListboxHeading, { children: heading }),
        search && /* @__PURE__ */ jsx(ListboxSearch, { children: /* @__PURE__ */ jsx(
          Input,
          {
            ref: searchRef,
            appearance: "bare",
            onChange: handleInputChange,
            magnitude: "large",
            placeholder: placeholder || `Filter ${itemsLabel}…`,
            type: "search",
            variant: filteredOptions.length === 0 ? "only" : "top",
            value: query
          }
        ) }),
        filteredOptions.length > 0 ? /* @__PURE__ */ jsx(ListboxList, { ref: listRef, search, children: entries(groupedOptions).map(([group, items]) => /* @__PURE__ */ jsxs(ListboxGroup, { groupVariant, children: [
          showGroupHeading && /* @__PURE__ */ jsx(ListboxGroupHeading, { children: group }),
          items.map((item) => /* @__PURE__ */ jsx(
            ListboxOption,
            {
              disabled: item.disabled,
              indent: showGroupHeading,
              highlighted: highlightedIndex >= 0 && enabledOptions.indexOf(item) === highlightedIndex,
              index: enabledOptions.indexOf(item),
              info: item[infoKey],
              listRef,
              multi,
              onHighlight: setHighlightedIndex,
              onSelect: handleSelect,
              selected: multi ? values$1.includes(item[valueKey]) : item[valueKey] === value,
              value: item[valueKey],
              children: renderItem ? renderItem(item) : item[labelKey] || item[valueKey]
            },
            item[valueKey]
          ))
        ] }, group)) }) : query !== "" && /* @__PURE__ */ jsx(ListboxVoid, { children: emptyText || `There are no matching ${itemsLabel}.` }),
        options.length === 0 && !search && /* @__PURE__ */ jsxs(ListboxVoid, { children: [
          "There are no ",
          itemsLabel,
          " available."
        ] })
      ]
    }
  );
};
Listbox.propTypes = propTypes;
Listbox.defaultProps = defaultProps;
export {
  Listbox
};
